/**
 * M7 S39: "Transit Priority", the finale flow's story link (spec §8.1–§8.4, §3.1, PC-7; plan S39). The Finale screen
 * (`src/ui/story/Finale.tsx`) reads a `FinaleSource`; this module builds one over the story service.
 *
 * - **The offer:** each path with `payroll.ts finaleOfferRefusal` (the ladder's typed refusals, plus "on the
 *   payroll" before the first commit). `noBundle` is not a refusal here: the flow picks the bundle before the commit.
 * - **The askers (§3.1):** `contacts.ts contactFor` per path (Expose through `exposeHandInContact`, so a wanted
 *   pilot never docks for the hearing); the asker's node from `finale.json askers`. A ship fallback (Voss's yacht,
 *   the Meridian courier) spawns its story ship at the player's system once (`spawnStoryShip`).
 * - **Voss's "we both sold it" (PC-12, §17 rule 1):** the node follows real career data only: the corporation line
 *   with a corporation, the sale line with a real tetherstone sale, else the clean line.
 * - **The roll call:** `rollCall(career, to)` node by node.
 * - **The hand-in:** S34's bundle picker's `handIn` effect → `bundle.ts pickBundle`.
 * - **The commit:** `commitFinale`, then `afterFinaleCommit` (the epilogue's countdowns, the Candle-dark event, the
 *   Sell bundle's originals to Meridian), then the corporation's ballot (`reapply.ts ballotStances`: the default
 *   for the paths in force with the corporation screen's toggle over it), as one result through the service, so its
 *   `autosave` effect writes the slot once (T24).
 * - **Expose → Guardians (S39d):** `exposeWeightDrop` lowers the pending Expose vote by the added credibility
 *   penalty (`lowerVoteWeight`, part of the same result).
 * - **The stance toggle (§8.2):** `setBallotStance` stores the override (`finale.ballot`) and sends `setPlayerBallot`.
 *
 * Event-driven (UI actions); never per frame.
 */
import { z } from 'zod'
import type { Career } from '../career/types'
import { pickBundle } from '../story/bundle'
import { caseContextOf, credibilityOf } from '../story/case'
import type { CommsView } from '../story/comms'
import { contactFor, type ContactResolution, type ContactView } from '../story/contacts'
import { epilogue, afterFinaleCommit, type EpilogueFacts } from '../story/finale/epilogue'
import { commitFinale, type FinaleRefusal } from '../story/finale/ladder'
import { exposeHandInContact, finaleData, type FinaleData, type FinaleInput, type PathMove } from '../story/finale/paths'
import { finaleOfferRefusal, type FinaleOfferRefusal } from '../story/finale/payroll'
import { rollCall } from '../story/finale/rollCall'
import { pathsInForce } from '../story/clock'
import { ballotStances } from '../story/reapply'
import { storyOf, withStory } from '../story/state'
import {
  BALLOT_STANCES,
  type CareerStage,
  FINALE_PATHS,
  STORY_SHIP_ROLES,
  type BallotStance,
  type BundleEntry,
  type FinalePath,
  type StoryCommand,
  type StoryResult,
} from '../story/types'
import { MEASURE_NAMES, STORY_WIRE_OUTLETS, type MeasureName, type StoryWireOutlet } from '../strategic/types'
import finaleJson from './finale.json'
import { vossPointingLine, type VossNames } from './missions/m3'
import type { StoryService } from './service'

// ---------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------

const askerSchema = z.strictObject({
  interaction: z.enum(['exposeHandIn', 'blackmail', 'guardians', 'sell']),
  nodes: z.record(z.string().min(1), z.string().min(1)),
  accept: z.array(z.string().min(1)).min(1),
})

const finaleLinkSchema = z.object({
  askers: z.strictObject({ expose: askerSchema, blackmail: askerSchema, guardians: askerSchema, sell: askerSchema }),
  shipRoles: z.record(z.string().min(1), z.enum(STORY_SHIP_ROLES)),
  bothSold: z.strictObject({ reply: z.string().min(1), sold: z.string().min(1), corporation: z.string().min(1), clean: z.string().min(1) }),
  candleHint: z.string().min(1),
})
export type FinaleLinkConfig = z.infer<typeof finaleLinkSchema>

let cfgCache: FinaleLinkConfig | null = null
export function finaleLinkConfig(): FinaleLinkConfig {
  cfgCache ??= finaleLinkSchema.parse(finaleJson)
  return cfgCache
}

// ---------------------------------------------------------------------------------------------
// Pure pieces
// ---------------------------------------------------------------------------------------------

export interface FinaleOffer {
  path: FinalePath
  /** Null = on offer now (a missing bundle is picked in the flow). */
  refusal: FinaleOfferRefusal | null
}

/** Every path with its refusal now (the finale offer and, after a commit, the ladder). */
export function finaleOffers(career: Career, data: FinaleData = finaleData()): FinaleOffer[] {
  return FINALE_PATHS.map((path) => {
    const r = finaleOfferRefusal(career, path, data.finale, data.case)
    return { path, refusal: r === 'noBundle' ? null : r }
  })
}

/** True when any path is on offer (the flow opens only then). */
export function finaleOpen(career: Career, data: FinaleData = finaleData()): boolean {
  return finaleOffers(career, data).some((o) => o.refusal === null)
}

export interface FinaleAsker {
  path: FinalePath
  contact: ContactResolution
  /** The asker's comms node. */
  nodeId: string
  /** The replies that take the offer. */
  accept: readonly string[]
  /** A ship fallback's story ship role (Voss's yacht, the Meridian courier), or null. */
  shipRole: (typeof STORY_SHIP_ROLES)[number] | null
}

/** Who asks for `path` now and where (§3.1: the primary contact or its first reachable fallback). */
export function askerFor(career: Career, view: ContactView, path: FinalePath, cfg: FinaleLinkConfig = finaleLinkConfig()): FinaleAsker {
  const a = cfg.askers[path]
  const contact = path === 'expose' ? exposeHandInContact(career, view) : contactFor(career, view, a.interaction)
  const nodeId = a.nodes[contact.option.id] ?? Object.values(a.nodes)[0] ?? ''
  return { path, contact, nodeId, accept: a.accept, shipRole: cfg.shipRoles[contact.option.id] ?? null }
}

/** Voss's answer to "You sold it." from real career data only (§17 rule 1). */
export function bothSoldNode(career: Career, corporation: boolean, cfg: FinaleLinkConfig = finaleLinkConfig()): string {
  if (corporation) return cfg.bothSold.corporation
  return (career.tetherSales?.length ?? 0) > 0 ? cfg.bothSold.sold : cfg.bothSold.clean
}

/**
 * D-438: Voss's pointing line before her finale asker node (the Blackmail path's), when the M3 gates now allow it
 * (`m3.ts vossPointingLine`, real career data only); null for any other node or while the gates hold it back.
 */
export function vossFinaleLines(career: Career, nodeId: string, stage: CareerStage, names: VossNames, cfg: FinaleLinkConfig = finaleLinkConfig()): string[] | null {
  if (!Object.values(cfg.askers.blackmail.nodes).includes(nodeId)) return null
  const line = vossPointingLine(career, stage, names)
  return line === null ? null : [line]
}

/** The roll call's nodes before committing `to`, in order. */
export function rollCallNodes(career: Career, to: FinalePath, data: FinaleData = finaleData()): string[] {
  return rollCall(career, to, data.finale).map((e) => e.nodeId)
}

/** Expose and Sell hand over a bundle. */
export function needsBundle(path: FinalePath): boolean {
  return path === 'expose' || path === 'sell'
}

/** The move `to` would make: `first`, or `<from>-<to>` (the cost text's key). */
export function moveKey(career: Career, to: FinalePath): string {
  const inForce = pathsInForce(storyOf(career).finale)
  if (inForce.length === 0) return `first-${to}`
  const from = inForce.includes('blackmail') ? 'blackmail' : inForce[inForce.length - 1]
  return `${from ?? 'first'}-${to}`
}

/** Where the commit is staged (§8.1, PC-7): the Candle when the player is there and it is lit, else the asker's place. */
export type FinaleStaging = 'candle' | 'primary' | 'fallback'

export function finaleStaging(career: Career, asker: FinaleAsker, atCandle: boolean): FinaleStaging {
  if (atCandle && storyOf(career).finale.candleDarkOnlineDay === undefined) return 'candle'
  return asker.contact.primary ? 'primary' : 'fallback'
}

export type TransitResult = { ok: true; result: StoryResult<Career>; move: PathMove } | { ok: false; reason: FinaleRefusal }

/** The corporation's ballot commands for the paths in force (with the toggle over the default). None without one. */
export function corpBallot(career: Career, corpFaction: FinaleInput['view']['corpFaction']): StoryCommand[] {
  if (corpFaction === null) return []
  const stances = ballotStances(storyOf(career))
  if (stances === undefined) return []
  const out: StoryCommand[] = []
  for (const measure of MEASURE_NAMES) {
    const stance = stances[measure]
    if (stance !== undefined) out.push({ type: 'setPlayerBallot', measure, stance })
  }
  return out
}

/**
 * S39d (spec §8.3, integration notes S21): Expose → Guardians lowers the pending Expose vote by what the move adds to
 * the hearing's credibility penalty (`case.json credibility.sponsorGuardian`, 0.1, within `credibility.cap`). None
 * for any other move, or when the penalty was already counted. The worker clamps the weight at 0 and refuses
 * (`noPendingVote`) once the vote is resolved.
 */
export function exposeWeightDrop(before: Career, after: Career, move: PathMove, data: FinaleData = finaleData()): StoryCommand[] {
  if (move.from !== 'expose' || move.to !== 'guardians') return []
  const cfg = data.case.case
  const drop = credibilityOf(caseContextOf(after), cfg) - credibilityOf(caseContextOf(before), cfg)
  // Rounded to the case's precision (0.1 + 0.1 − 0.1 must not send 0.09999...).
  const rounded = Math.round(drop * 1e6) / 1e6
  return rounded > 0 ? [{ type: 'lowerVoteWeight', measure: 'tetherstoneBan', drop: rounded }] : []
}

/** The whole commit (pure): the ladder's commit, the aftermath and the corporation's ballot, as one result. */
export function transitPriority(career: Career, to: FinalePath, input: FinaleInput, data: FinaleData = finaleData()): TransitResult {
  const r = commitFinale(career, to, input, data)
  if (!r.ok) return r
  const after = afterFinaleCommit(r.result.career, r.move, data.finale)
  const ballot = corpBallot(after.career, input.view.corpFaction)
  const weight = exposeWeightDrop(career, after.career, r.move, data)
  return {
    ok: true,
    move: r.move,
    result: {
      career: after.career,
      commands: [...r.result.commands, ...after.commands, ...ballot, ...weight],
      effects: [...r.result.effects, ...after.effects],
    },
  }
}

/** The corporation screen's toggle: stores the override and sends the stance (refused before the first commit). */
export function setBallotStance(career: Career, measure: MeasureName, stance: BallotStance): StoryResult<Career> | null {
  if (storyOf(career).finale.paths.length === 0 || !BALLOT_STANCES.includes(stance)) return null
  const next = withStory(career, (s) => ({ ...s, finale: { ...s.finale, ballot: { ...s.finale.ballot, [measure]: stance } } }))
  return { career: next, commands: [{ type: 'setPlayerBallot', measure, stance }], effects: [] }
}

/** The stance the corporation casts on `measure` now (null before the first commit). */
export function ballotStanceOf(career: Career, measure: MeasureName): BallotStance | null {
  return ballotStances(storyOf(career))?.[measure] ?? null
}

export interface EpilogueLine {
  headline: string
  outlet: StoryWireOutlet | null
}

const OUTLETS: ReadonlySet<string> = new Set(STORY_WIRE_OUTLETS)

function isOutlet(v: string | undefined): v is StoryWireOutlet {
  return v !== undefined && OUTLETS.has(v)
}

/** The Wire epilogue as it reads with today's facts (the commit screen's preview; the Wire posts it over the days). */
export function epilogueLines(career: Career, facts: EpilogueFacts, data: FinaleData = finaleData()): EpilogueLine[] {
  return epilogue(career, facts, data.finale).map((p) => ({ headline: p.headline, outlet: isOutlet(p.source) ? p.source : null }))
}

// ---------------------------------------------------------------------------------------------
// The source the Finale screen reads
// ---------------------------------------------------------------------------------------------

export interface FinaleSource {
  offers(): FinaleOffer[]
  /** The asker for `path`; with `spawn`, a ship fallback's ship is spawned (once per link). */
  ask(path: FinalePath, spawn: boolean): FinaleAsker | null
  /** A comms node as the player sees it now (the comms port's `view`). */
  node(nodeId: string): CommsView | null
  /** Voss's answer node when `replyId` is "You sold it." (real career data only), else null. */
  bothSold(replyId: string): string | null
  rollCall(path: FinalePath): string[]
  staging(path: FinalePath): FinaleStaging
  moveKey(path: FinalePath): string
  /** The S34 picker's hand-in: records the bundle for `path`. */
  handIn(path: FinalePath, entries: readonly BundleEntry[]): boolean
  commit(path: FinalePath): TransitResult | { ok: false; reason: 'noCareer' }
  epilogue(): EpilogueLine[]
}

export interface FinaleLinkDeps {
  service: Pick<StoryService, 'apply'>
  career: () => Career | null
  node: (nodeId: string) => CommsView | null
  contactView: () => ContactView
  /** The commit input from the worker and the player's ship. */
  input: () => FinaleInput
  /** The epilogue's sim facts today. */
  facts: () => EpilogueFacts
  /** True while the player is at the Candle (PC-7: the Harrow system). */
  atCandle?: () => boolean
  data?: FinaleData
  config?: FinaleLinkConfig
}

function nothing(career: Career): StoryResult<Career> {
  return { career, commands: [], effects: [] }
}

export function createFinaleLink(deps: FinaleLinkDeps): FinaleSource {
  const data = deps.data ?? finaleData()
  const cfg = deps.config ?? finaleLinkConfig()
  const spawned = new Set<string>()

  function ask(path: FinalePath, spawn: boolean): FinaleAsker | null {
    const c = deps.career()
    if (c === null) return null
    const a = askerFor(c, deps.contactView(), path, cfg)
    if (spawn && a.shipRole !== null && !spawned.has(a.shipRole)) {
      spawned.add(a.shipRole)
      const cmd: StoryCommand = { type: 'spawnStoryShip', key: `finale:${a.shipRole}`, role: a.shipRole, systemId: deps.input().systemId }
      deps.service.apply(`story finale ship ${a.shipRole}`, (career) => ({ career, commands: [cmd], effects: [] }))
    }
    return a
  }

  return {
    offers: () => {
      const c = deps.career()
      return c === null ? [] : finaleOffers(c, data)
    },
    ask,
    node: (id) => deps.node(id),
    bothSold(replyId) {
      const c = deps.career()
      if (replyId !== cfg.bothSold.reply) return null
      return c === null ? cfg.bothSold.clean : bothSoldNode(c, deps.input().view.corpFaction !== null, cfg)
    },
    rollCall(path) {
      const c = deps.career()
      return c === null ? [] : rollCallNodes(c, path, data)
    },
    staging(path) {
      const c = deps.career()
      const a = c === null ? null : askerFor(c, deps.contactView(), path, cfg)
      return c === null || a === null ? 'primary' : finaleStaging(c, a, deps.atCandle?.() ?? false)
    },
    moveKey(path) {
      const c = deps.career()
      return c === null ? `first-${path}` : moveKey(c, path)
    },
    handIn(path, entries) {
      const c = deps.career()
      if (c === null || !pickBundle(c, path, entries, data.case).ok) return false
      const r = deps.service.apply(`story bundle ${path}`, (career) => {
        const p = pickBundle(career, path, entries, data.case)
        return p.ok ? nothing(p.career) : nothing(career)
      })
      return r !== null
    },
    commit(path) {
      const c = deps.career()
      if (c === null) return { ok: false, reason: 'noCareer' }
      const input = deps.input()
      const first = transitPriority(c, path, input, data)
      if (!first.ok) return first
      let move = first.move
      const r = deps.service.apply(`story finale ${path}`, (career) => {
        const again = transitPriority(career, path, input, data)
        if (!again.ok) return nothing(career)
        move = again.move
        return again.result
      })
      return r === null ? { ok: false, reason: 'noCareer' } : { ok: true, result: r, move }
    },
    epilogue() {
      const c = deps.career()
      return c === null ? [] : epilogueLines(c, deps.facts(), data)
    },
  }
}
